<template>
  <b-modal
    id="wiki-import-modal"
    ref="modal"
    :title="importType === 'kb' ? 'Import Knowledge Base Articles' : importType === 'templates' ? 'Import Templates' : 'Wiki Import'"
    size="xl"
    centered
    no-close-on-backdrop
    @hidden="onModalHidden"
    hide-footer
    modal-class="wiki-import-modal"
  >
    <!-- Progress Steps -->
    <div class="wizard-progress">
      <div
        v-for="(step, index) in steps"
        :key="step.key"
        class="progress-step"
        :class="{
          'active': currentStep === index,
          'completed': currentStep > index,
          'clickable': currentStep > index
        }"
        @click="goToStep(index)"
      >
        <div class="step-indicator">
          <i v-if="currentStep > index" class="fa fa-check"></i>
          <span v-else>{{ index + 1 }}</span>
        </div>
        <span class="step-label">{{ step.label }}</span>
      </div>
    </div>

    <!-- Step Content -->
    <div class="wizard-content">

      <!-- Step 0: Import Type Selection -->
      <div v-show="currentStep === 0" class="step-content">
        <h4 class="step-title">What would you like to import?</h4>
        <p class="step-subtitle">Choose the type of documents you're importing</p>

        <div class="import-type-cards">
          <div
            class="import-type-card"
            :class="{ selected: importType === 'templates' }"
            @click="importType = 'templates'"
          >
            <div class="card-icon">
              <i class="fa fa-comments fa-2x"></i>
            </div>
            <h5>SMS / Email Templates</h5>
            <p class="text-muted">Import a document containing SMS or email templates. AI will detect templates and map variables automatically.</p>
          </div>

          <div
            class="import-type-card"
            :class="{ selected: importType === 'kb' }"
            @click="importType = 'kb'"
          >
            <div class="card-icon">
              <i class="fa fa-book fa-2x"></i>
            </div>
            <h5>Knowledge Base Articles</h5>
            <p class="text-muted">Bulk import .docx or .pdf files as knowledge base articles. AI extracts text, images, and structure. Up to 100 documents.</p>
          </div>
        </div>

      </div>

      <!-- Template Path — Step 1: Upload -->
      <div v-show="importType === 'templates' && currentStep === 1" class="step-content">
        <h4 class="step-title">Upload Document</h4>
        <p class="step-subtitle">Upload a .docx file containing your SMS or email templates</p>

        <!-- Parsing spinner -->
        <div v-if="isParsing" class="parsing-state">
          <div class="parsing-spinner">
            <i class="fa fa-spinner fa-spin fa-3x"></i>
          </div>
          <h5>Analyzing your document...</h5>
          <p class="text-muted">Reading your templates, detecting variables, and identifying hardcoded values. This can take a minute or two.</p>
          <p class="text-muted mt-2" style="font-size: 0.85rem;">You can close this window and come back &mdash; we'll notify you when it's ready.</p>
        </div>

        <!-- Error state -->
        <div v-else-if="parseError" class="upload-error">
          <div class="error-icon">
            <i class="fa fa-exclamation-triangle"></i>
          </div>
          <h5>Something went wrong</h5>
          <p class="text-muted">{{ parseError }}</p>
          <b-button variant="outline-primary" @click="clearError">
            <i class="fa fa-refresh mr-1"></i> Try Again
          </b-button>
        </div>

        <!-- Upload zone + format preview -->
        <div v-else class="upload-layout">
          <div
            class="upload-zone"
            :class="{ 'drag-over': isDragOver }"
            @dragover="onDragOver"
            @dragleave="onDragLeave"
            @drop="onDrop"
          >
            <div class="upload-icon">
              <i class="fa fa-cloud-upload"></i>
            </div>
            <h5>Drag &amp; drop your .docx file here</h5>
            <p class="text-muted mb-3">Only .docx files are supported</p>
            <div class="upload-divider">
              <span>or</span>
            </div>
            <b-button variant="primary" class="mt-3" @click="triggerFileInput">
              <i class="fa fa-folder-open mr-1"></i> Browse Files
            </b-button>
            <input
              ref="fileInput"
              type="file"
              accept=".docx"
              style="display: none;"
              @change="onFileSelected"
            />
          </div>

          <!-- Format Preview -->
          <div class="format-preview">
            <div class="format-preview-header">
              <i class="fa fa-file-text-o mr-1"></i> Expected Format
            </div>
            <div class="format-preview-body">
              <div
                v-for="(sample, sIdx) in formatSamples"
                :key="sIdx"
                class="format-sample"
                v-html="sample"
              ></div>
            </div>
            <a href="#" class="download-sample-link" @click.prevent="downloadSample">
              <i class="fa fa-download mr-1"></i> Download sample .docx
            </a>
          </div>
        </div>

        <!-- File selected indicator (after successful parse, if user navigates back) -->
        <div v-if="parsedResult && !isParsing && !parseError" class="file-selected-indicator mt-3">
          <i class="fa fa-check-circle text-success mr-2"></i>
          <strong>{{ fileName }}</strong> parsed successfully
          <span class="text-muted ml-2">({{ templates.length }} templates found)</span>
        </div>
      </div>

      <!-- KB Path — Step 1: Upload Files -->
      <div v-show="importType === 'kb' && currentStep === 1" class="step-content">
        <h4 class="step-title">Upload Documents</h4>
        <p class="step-subtitle">Upload up to 100 .docx or .pdf files (max 25 MB each)</p>

        <div v-if="kbIsParsing" class="parsing-state">
          <div class="parsing-spinner">
            <i class="fa fa-spinner fa-spin fa-3x"></i>
          </div>
          <h5 v-if="kbProgress && kbProgress.phase === 'zipping'">
            Packaging {{ kbFiles.length }} files...
          </h5>
          <h5 v-else-if="kbProgress && kbProgress.phase === 'uploading'">
            Uploading to server...
          </h5>
          <h5 v-else-if="kbProgress">
            Processing {{ kbProgress.processed }} of {{ kbProgress.total }} documents...
          </h5>
          <h5 v-else>Starting document processing...</h5>
          <p v-if="kbProgress && kbProgress.failed > 0" class="text-warning">
            {{ kbProgress.failed }} document(s) failed
          </p>
          <p class="text-muted mt-2" style="font-size: 0.85rem;">
            You can close this window and come back — we'll notify you when it's ready.
          </p>
        </div>

        <div v-else-if="kbParseError" class="upload-error">
          <div class="error-icon">
            <i class="fa fa-exclamation-triangle"></i>
          </div>
          <h5>Something went wrong</h5>
          <p class="text-muted">{{ kbParseError }}</p>
          <b-button variant="outline-primary" @click="kbParseError = null">
            <i class="fa fa-refresh mr-1"></i> Try Again
          </b-button>
        </div>

        <div v-else>
          <div
            class="upload-zone"
            :class="{ 'drag-over': kbIsDragOver }"
            @dragover="kbOnDragOver"
            @dragleave="kbOnDragLeave"
            @drop="kbOnDrop"
            @click="$refs.kbFileInput.click()"
          >
            <div v-if="kbFiles.length === 0">
              <i class="fa fa-cloud-upload fa-3x text-muted"></i>
              <p class="mt-2">Drag &amp; drop .docx or .pdf files here, or click to browse</p>
              <p class="text-muted" style="font-size: 0.85rem;">Up to 100 files, max 25 MB each</p>
            </div>
            <div v-else>
              <p class="mb-1"><strong>{{ kbFiles.length }} file(s) selected</strong></p>
              <p class="text-muted mb-0" style="font-size: 0.85rem;">
                {{ kbTotalSize }} total — click or drop to add more
              </p>
            </div>
          </div>
          <input
            ref="kbFileInput"
            type="file"
            accept=".docx,.pdf"
            multiple
            style="display: none;"
            @change="kbOnFileSelected"
          />

          <div v-if="kbFiles.length > 0" class="kb-file-list mt-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <small class="text-muted">{{ kbFiles.length }} file(s)</small>
              <b-button size="sm" variant="outline-danger" @click="kbFiles = []">
                Clear All
              </b-button>
            </div>
            <div
              v-for="(file, idx) in kbFiles"
              :key="idx"
              class="kb-file-item d-flex justify-content-between align-items-center"
            >
              <span class="text-truncate" style="max-width: 80%;">
                <i class="fa fa-file-word-o mr-1"></i> {{ file.name }}
              </span>
              <span>
                <small class="text-muted mr-2">{{ formatFileSize(file.size) }}</small>
                <i class="fa fa-times text-danger" style="cursor:pointer;" @click="kbFiles.splice(idx, 1)"></i>
              </span>
            </div>
          </div>

          <!-- Large batch info -->
          <div v-if="kbTotalSizeBytes > 3.5 * 1024 * 1024" class="kb-size-info mt-3">
            <small class="text-muted">
              <i class="fa fa-info-circle mr-1"></i>
              Large batch detected ({{ kbTotalSize }}). Files will be automatically packaged and uploaded via S3.
            </small>
          </div>

          <div class="wizard-actions mt-4">
            <b-button variant="outline-secondary" @click="previousStep">
              <i class="fa fa-arrow-left mr-1"></i> Back
            </b-button>
            <b-button
              variant="primary"
              :disabled="kbFiles.length === 0"
              @click="kbUploadAndParse"
            >
              Upload &amp; Parse <i class="fa fa-arrow-right ml-1"></i>
            </b-button>
          </div>
        </div>
      </div>

      <!-- KB Path — Step 2: Review Documents -->
      <div v-show="importType === 'kb' && currentStep === 2" class="step-content">
        <h4 class="step-title">Review Documents</h4>
        <p class="step-subtitle">
          {{ kbSuccessCount }} document(s) parsed successfully
          <span v-if="kbFailedCount > 0" class="text-danger">
            ({{ kbFailedCount }} failed)
          </span>
        </p>

        <div class="d-flex justify-content-between align-items-center mb-3" style="padding-left: 18px;">
          <div>
            <b-form-checkbox
              :checked="kbAllSelected"
              @change="kbToggleSelectAll"
            >
              Select All ({{ kbSelectedCount }} of {{ kbSuccessCount }})
            </b-form-checkbox>
          </div>
        </div>

        <div class="kb-review-list" style="max-height: 400px; overflow-y: auto;">
          <div
            v-for="(doc, idx) in kbDocuments"
            :key="idx"
            class="kb-review-item"
            :class="{ 'failed': doc.status === 'failed' }"
          >
            <div class="d-flex align-items-start">
              <b-form-checkbox
                v-if="doc.status === 'success'"
                v-model="doc.selected"
                class="mr-2 mt-1"
              />
              <div v-else class="mr-2 mt-1" style="width: 20px;">
                <i class="fa fa-exclamation-circle text-danger"></i>
              </div>
              <div class="flex-grow-1">
                <div v-if="doc.status === 'success'" class="d-flex align-items-center mb-1">
                  <div style="max-width: 400px; flex-grow: 1;">
                    <label class="mb-0" style="font-size: 0.75rem; color: #718096;">Wiki Page Title</label>
                    <input
                      v-model="doc.editTitle"
                      class="form-control form-control-sm"
                      placeholder="Enter wiki page title"
                    />
                  </div>
                  <small class="text-muted ml-3" style="white-space: nowrap;">
                    <template v-if="doc.documentType === 'pdf-attachment'">
                      <i class="fa fa-file-pdf-o"></i> PDF
                      <span v-if="doc.fileSize"> &middot; {{ formatFileSize(doc.fileSize) }}</span>
                    </template>
                    <template v-else>
                      {{ doc.wordCount }} words
                      <span v-if="doc.imageCount" class="ml-1">
                        <i class="fa fa-image"></i> {{ doc.imageCount }}
                      </span>
                    </template>
                  </small>
                </div>
                <div v-else class="d-flex align-items-center mb-1">
                  <span class="text-muted">{{ doc.filename }}</span>
                  <small v-if="doc.error" class="text-danger ml-2">{{ doc.error }}</small>
                </div>
                <small v-if="doc.status === 'success'" class="text-muted">{{ doc.filename }}</small>
              </div>
            </div>
          </div>
        </div>

        <div class="wizard-actions mt-4">
          <b-button variant="outline-secondary" @click="previousStep">
            <i class="fa fa-arrow-left mr-1"></i> Back
          </b-button>
          <b-button
            variant="primary"
            :disabled="kbSelectedCount === 0"
            @click="nextStep"
          >
            Import {{ kbSelectedCount }} Document(s) <i class="fa fa-arrow-right ml-1"></i>
          </b-button>
        </div>
      </div>

      <!-- KB Path — Step 3: Import -->
      <div v-show="importType === 'kb' && currentStep === 3" class="step-content">
        <h4 class="step-title">
          <span v-if="kbIsImporting">Importing Documents...</span>
          <span v-else-if="kbImportResults.length > 0">Import Complete</span>
          <span v-else>Confirm Import</span>
        </h4>

        <div v-if="!kbIsImporting && kbImportResults.length === 0">
          <p class="text-muted" style="font-size: 0.95rem;">
            <strong>{{ kbSelectedCount }}</strong> document(s) will be imported as knowledge base articles.
          </p>
          <div class="wizard-actions mt-4">
            <b-button variant="outline-secondary" @click="previousStep">
              <i class="fa fa-arrow-left mr-1"></i> Back
            </b-button>
            <b-button variant="primary" @click="kbImportDocuments">
              <i class="fa fa-check mr-1"></i> Import Now
            </b-button>
          </div>
        </div>

        <div v-if="kbIsImporting">
          <p>Importing {{ kbImportProgress }} of {{ kbImportTotal }}...</p>
          <b-progress :value="kbImportProgress" :max="kbImportTotal" animated class="mb-3"></b-progress>
        </div>

        <div v-if="!kbIsImporting && kbImportResults.length > 0">
          <div class="alert" :class="kbImportFailed === 0 ? 'alert-success' : 'alert-warning'">
            <strong>{{ kbImportSucceeded }}</strong> imported successfully.
            <span v-if="kbImportFailed > 0">
              <strong>{{ kbImportFailed }}</strong> failed.
            </span>
          </div>

          <div style="max-height: 300px; overflow-y: auto;">
            <div
              v-for="(result, idx) in kbImportResults"
              :key="idx"
              class="d-flex align-items-center py-1"
            >
              <i
                :class="result.success ? 'fa fa-check-circle text-success' : 'fa fa-times-circle text-danger'"
                class="mr-2"
              ></i>
              <span>{{ result.title }}</span>
              <small v-if="result.error" class="text-danger ml-2">{{ result.error }}</small>
            </div>
          </div>

          <div class="wizard-actions mt-4">
            <div></div>
            <b-button variant="primary" @click="finishImport">
              Done
            </b-button>
          </div>
        </div>
      </div>

      <!-- Template Path — Step 2: Review Templates -->
      <div v-show="importType === 'templates' && currentStep === 2" class="step-content">
        <h4 class="step-title">Review Templates</h4>
        <p class="step-subtitle">{{ templates.length }} templates found in <strong>{{ fileName }}</strong></p>

        <!-- Select All row -->
        <div class="select-all-row">
          <b-form-checkbox
            :checked="allSelected"
            @change="toggleSelectAll"
          >
            Select All
          </b-form-checkbox>
          <span class="selected-counter">{{ selectedCount }} of {{ templates.length }} selected</span>
        </div>

        <!-- Template cards -->
        <div class="template-list">
          <div
            v-for="(template, tIdx) in templates"
            :key="tIdx"
            class="template-card"
            :class="{ 'excluded': !template.included }"
          >
            <div class="template-card-header">
              <b-form-checkbox
                v-model="template.included"
                class="template-checkbox"
              />
              <b-form-input
                v-model="template.title"
                class="template-title-input"
                placeholder="Template title..."
                size="sm"
              />
              <div class="type-badges">
                <span
                  class="type-badge"
                  :class="{
                    'type-sms': template.suggestedType === 'sms' || template.suggestedType === 'both',
                    'type-email': template.suggestedType === 'email',
                    'type-inactive': template.suggestedType !== 'sms' && template.suggestedType !== 'both'
                  }"
                  @click="toggleType(template, 'sms')"
                  v-b-tooltip.hover
                  title="Click to toggle SMS type"
                >
                  <i class="fa fa-comment"></i> SMS
                </span>
                <span
                  class="type-badge"
                  :class="{
                    'type-email': template.suggestedType === 'email' || template.suggestedType === 'both',
                    'type-sms': false,
                    'type-inactive': template.suggestedType !== 'email' && template.suggestedType !== 'both'
                  }"
                  @click="toggleType(template, 'email')"
                  v-b-tooltip.hover
                  title="Click to toggle Email type"
                >
                  <i class="fa fa-envelope"></i> Email
                </span>
              </div>
            </div>

            <div class="template-card-body">
              <p class="template-body-text">
                {{ template._expanded ? template.body : truncate(template.body, 150) }}
              </p>
              <a
                v-if="template.body && template.body.length > 150"
                href="#"
                class="show-more-link"
                @click.prevent="$set(template, '_expanded', !template._expanded)"
              >
                {{ template._expanded ? 'Show less' : 'Show more' }}
              </a>
            </div>

            <div v-if="template.detectedVariables && template.detectedVariables.length > 0" class="template-card-footer">
              <span
                v-for="(varName, vIdx) in template.detectedVariables"
                :key="vIdx"
                class="variable-badge"
              >
                {{ varName }}
              </span>
            </div>

            <div v-if="template.typeReason" class="type-reason">
              <i class="fa fa-info-circle mr-1"></i>{{ template.typeReason }}
            </div>
          </div>
        </div>
      </div>

      <!-- Template Path — Step 3: Map Variables -->
      <div v-show="importType === 'templates' && currentStep === 3" class="step-content">
        <h4 class="step-title">Map Variables</h4>
        <p class="step-subtitle">Review how document variables will be replaced with Teamio merge fields</p>

        <!-- Variable Mappings -->
        <div v-if="variableMappingEntries.length > 0" class="mapping-section">
          <h6 class="section-label mb-3">
            <i class="fa fa-exchange mr-2"></i>Variable Mappings
          </h6>
          <div class="mapping-table">
            <div class="mapping-header">
              <div class="mapping-col col-var">Document Variable</div>
              <div class="mapping-col col-used">Used In</div>
              <div class="mapping-col col-map">Map To</div>
              <div class="mapping-col col-conf">Confidence</div>
            </div>
            <div
              v-for="(entry, mIdx) in variableMappingEntries"
              :key="mIdx"
              class="mapping-row"
            >
              <div class="mapping-col col-var">
                <code>{{ entry.varName }}</code>
              </div>
              <div class="mapping-col col-used">
                <span class="usage-count">{{ countVariableUsage(entry.varName) }} template{{ countVariableUsage(entry.varName) !== 1 ? 's' : '' }}</span>
              </div>
              <div class="mapping-col col-map">
                <select
                  v-model="entry.mapping.userChoice"
                  class="mapping-select form-control form-control-sm"
                >
                  <optgroup v-if="entry.mapping.suggestedMapping" label="Suggested">
                    <option :value="entry.mapping.suggestedMapping">{{ entry.mapping.suggestedMapping }}</option>
                  </optgroup>
                  <optgroup
                    v-for="group in variableOptions"
                    :key="group.label"
                    :label="group.label"
                  >
                    <option
                      v-for="opt in group.options"
                      :key="opt.value"
                      :value="opt.value"
                    >{{ opt.label }}</option>
                  </optgroup>
                </select>
              </div>
              <div class="mapping-col col-conf">
                <span
                  class="confidence-badge"
                  :class="{
                    'conf-high': entry.mapping.confidence === 'high',
                    'conf-medium': entry.mapping.confidence === 'medium',
                    'conf-low': entry.mapping.confidence === 'low'
                  }"
                >
                  {{ entry.mapping.confidence }}
                </span>
                <i
                  v-if="entry.mapping.note"
                  class="fa fa-info-circle ml-1 text-muted"
                  v-b-tooltip.hover
                  :title="entry.mapping.note"
                ></i>
              </div>
            </div>
          </div>
        </div>

        <!-- Hardcoded Values -->
        <div v-if="hardcodedValues.length > 0" class="hardcoded-section mt-4">
          <h6 class="section-label mb-3">
            <i class="fa fa-pencil-square-o mr-2"></i>Hardcoded Values
            <span class="text-muted font-weight-normal ml-2" style="font-size: 0.8rem;">
              (Check items you want to replace with merge fields)
            </span>
          </h6>
          <div
            v-for="(hv, hvIdx) in hardcodedValues"
            :key="hvIdx"
            class="hardcoded-row"
          >
            <b-form-checkbox v-model="hv.apply" class="hv-checkbox" />
            <div class="hv-value">
              <code>{{ hv.value }}</code>
              <span class="hv-occurrences">{{ hv.occurrences }} occurrence{{ hv.occurrences !== 1 ? 's' : '' }}</span>
            </div>
            <div class="hv-arrow">
              <i class="fa fa-long-arrow-right"></i>
            </div>
            <div class="hv-replacement">
              <code :class="{ 'text-muted': !hv.apply }">{{ hv.suggestedVariable || '--' }}</code>
            </div>
            <i
              v-if="hv.note"
              class="fa fa-info-circle text-muted ml-2"
              v-b-tooltip.hover
              :title="hv.note"
            ></i>
          </div>
        </div>

        <!-- No variables detected -->
        <div v-if="variableMappingEntries.length === 0 && hardcodedValues.length === 0" class="no-variables-message">
          <i class="fa fa-check-circle text-success fa-2x mb-2"></i>
          <h6>No variables detected</h6>
          <p class="text-muted">Your templates don't contain any variables or hardcoded values that need mapping. You can proceed to the next step.</p>
        </div>
      </div>

      <!-- Template Path — Step 4: Confirm & Import -->
      <div v-show="importType === 'templates' && currentStep === 4" class="step-content">
        <h4 class="step-title">Confirm &amp; Import</h4>

        <!-- Pre-import state -->
        <template v-if="!isImporting && importResults.length === 0">
          <p class="step-subtitle">Review your import before creating wiki pages</p>

          <!-- Summary bar -->
          <div class="import-summary-bar">
            <div class="summary-stat">
              <span class="stat-number">{{ selectedCount }}</span>
              <span class="stat-label">Templates</span>
            </div>
            <div class="summary-stat">
              <span class="stat-number">{{ smsCount }}</span>
              <span class="stat-label">SMS</span>
            </div>
            <div class="summary-stat">
              <span class="stat-number">{{ emailCount }}</span>
              <span class="stat-label">Email</span>
            </div>
          </div>

          <!-- Preview list -->
          <div class="confirm-preview-list">
            <div
              v-for="(template, cIdx) in includedTemplates"
              :key="cIdx"
              class="confirm-preview-item"
            >
              <div class="confirm-item-header">
                <strong>{{ template.title }}</strong>
                <div class="confirm-type-badges">
                  <span v-if="template.suggestedType === 'sms' || template.suggestedType === 'both'" class="type-badge type-sms">
                    <i class="fa fa-comment"></i> SMS
                  </span>
                  <span v-if="template.suggestedType === 'email' || template.suggestedType === 'both'" class="type-badge type-email">
                    <i class="fa fa-envelope"></i> Email
                  </span>
                </div>
              </div>
              <p class="confirm-item-body">{{ truncate(applyMappings(template.body), 200) }}</p>
            </div>
          </div>
        </template>

        <!-- During import -->
        <template v-if="isImporting">
          <p class="step-subtitle">Creating wiki pages...</p>
          <div class="import-progress-section">
            <b-progress :value="importProgress" :max="importTotal" variant="primary" animated class="mb-3" />
            <p class="text-center text-muted">{{ importProgress }} of {{ importTotal }} imported</p>
          </div>
        </template>

        <!-- Post-import results -->
        <template v-if="!isImporting && importResults.length > 0">
          <p class="step-subtitle">Import complete</p>

          <div class="import-results-summary">
            <div class="result-stat result-success">
              <i class="fa fa-check-circle"></i>
              <span>{{ importSucceeded }} succeeded</span>
            </div>
            <div v-if="importFailed > 0" class="result-stat result-failed">
              <i class="fa fa-times-circle"></i>
              <span>{{ importFailed }} failed</span>
            </div>
          </div>

          <div class="import-results-list">
            <div
              v-for="(result, rIdx) in importResults"
              :key="rIdx"
              class="import-result-row"
              :class="{ 'result-error': !result.success }"
            >
              <i
                class="fa"
                :class="result.success ? 'fa-check-circle text-success' : 'fa-times-circle text-danger'"
              ></i>
              <span class="result-title">{{ result.title }}</span>
              <span v-if="!result.success" class="result-error-msg text-danger">{{ result.error }}</span>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Wizard Footer (template path only — KB path has inline actions) -->
    <div v-if="importType === 'templates' || currentStep === 0" class="wizard-footer">
      <b-button
        v-if="currentStep > 0"
        variant="outline-secondary"
        @click="previousStep"
        :disabled="isImporting || importResults.length > 0"
      >
        <i class="fa fa-arrow-left mr-1"></i> Previous
      </b-button>
      <div v-else></div>

      <div class="footer-right">
        <b-button
          v-if="importResults.length === 0"
          variant="outline-secondary"
          @click="hide"
          class="mr-2"
          :disabled="isImporting"
        >
          {{ isParsing ? 'Continue in Background' : 'Cancel' }}
        </b-button>

        <!-- Next button (steps 0-3) -->
        <b-button
          v-if="currentStep < steps.length - 1"
          variant="primary"
          @click="nextStep"
          :disabled="!canProceed"
        >
          Next <i class="fa fa-arrow-right ml-1"></i>
        </b-button>

        <!-- Import button (step 4, pre-import) -->
        <b-button
          v-else-if="currentStep === steps.length - 1 && !isImporting && importResults.length === 0"
          variant="success"
          @click="importTemplates"
          :disabled="!canProceed"
        >
          <i class="fa fa-download mr-1"></i>
          Import {{ selectedCount }} Template{{ selectedCount !== 1 ? 's' : '' }}
        </b-button>

        <!-- Done button (step 4, post-import) -->
        <b-button
          v-else-if="!isImporting && importResults.length > 0"
          variant="success"
          @click="finishImport"
        >
          <i class="fa fa-check mr-1"></i> Done
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
/* eslint-disable */
import { mapState, mapActions } from 'vuex';
import axios from 'axios';
import { extractApiError } from '@/utils/apiError';

export default {
  name: 'WikiImportModal',

  data() {
    return {
      currentStep: 0,
      importType: null, // null | 'templates' | 'kb'
      isParsing: false,
      parseError: null,
      fileName: null,
      parsedResult: null,
      templates: [],
      variableMappings: {},
      hardcodedValues: [],
      isImporting: false,
      importProgress: 0,
      importTotal: 0,
      importResults: [],
      isDragOver: false,
      isModalVisible: false,
      pollTimer: null,
      // KB import data
      kbFiles: [],
      kbIsParsing: false,
      kbParseError: null,
      kbJobId: null,
      kbPollTimer: null,
      kbProgress: null,
      kbDocuments: [],
      kbSummary: null,
      kbBulkTags: [],
      kbIsImporting: false,
      kbImportProgress: 0,
      kbImportTotal: 0,
      kbImportResults: [],
      kbIsDragOver: false,
    };
  },

  computed: {
    ...mapState({
      siteID: state => state.customer.siteID,
      userID: state => state.customer.userID,
    }),

    steps() {
      if (this.importType === 'kb') {
        return [
          { key: 'type', label: 'Import Type' },
          { key: 'upload', label: 'Upload Files' },
          { key: 'review', label: 'Review Documents' },
          { key: 'import', label: 'Import' }
        ];
      }
      return [
        { key: 'type', label: 'Import Type' },
        { key: 'upload', label: 'Upload' },
        { key: 'review', label: 'Review Templates' },
        { key: 'variables', label: 'Map Variables' },
        { key: 'confirm', label: 'Confirm & Import' }
      ];
    },

    allSelected() {
      return this.templates.length > 0 && this.templates.every(t => t.included);
    },

    selectedCount() {
      return this.templates.filter(t => t.included).length;
    },

    includedTemplates() {
      return this.templates.filter(t => t.included);
    },

    smsCount() {
      return this.includedTemplates.filter(t => t.suggestedType === 'sms' || t.suggestedType === 'both').length;
    },

    emailCount() {
      return this.includedTemplates.filter(t => t.suggestedType === 'email' || t.suggestedType === 'both').length;
    },

    canProceed() {
      if (this.currentStep === 0) return this.importType !== null;
      if (this.importType === 'kb') {
        switch (this.currentStep) {
          case 1: return this.kbDocuments.length > 0;
          case 2: return this.kbDocuments.some(d => d.selected && d.status === 'success');
          case 3: return !this.kbIsImporting;
          default: return false;
        }
      }
      // Template path (shifted by 1)
      switch (this.currentStep) {
        case 1: return this.parsedResult !== null;
        case 2: return this.templates.some(t => t.included);
        case 3: return true;
        case 4: return !this.isImporting;
        default: return false;
      }
    },

    // KB computed properties
    kbTotalSizeBytes() {
      return this.kbFiles.reduce((sum, f) => sum + f.size, 0);
    },
    kbTotalSize() {
      return this.formatFileSize(this.kbTotalSizeBytes);
    },
    kbSelectedCount() {
      return this.kbDocuments.filter(d => d.selected && d.status === 'success').length;
    },
    kbSuccessCount() {
      return this.kbDocuments.filter(d => d.status === 'success').length;
    },
    kbFailedCount() {
      return this.kbDocuments.filter(d => d.status === 'failed').length;
    },
    kbAllSelected() {
      const successDocs = this.kbDocuments.filter(d => d.status === 'success');
      return successDocs.length > 0 && successDocs.every(d => d.selected);
    },
    kbImportSucceeded() {
      return this.kbImportResults.filter(r => r.success).length;
    },
    kbImportFailed() {
      return this.kbImportResults.filter(r => !r.success).length;
    },

    variableMappingEntries() {
      return Object.keys(this.variableMappings).map(varName => ({
        varName,
        mapping: this.variableMappings[varName]
      }));
    },

    importSucceeded() {
      return this.importResults.filter(r => r.success).length;
    },

    importFailed() {
      return this.importResults.filter(r => !r.success).length;
    },

    formatSamples() {
      const v = (text) => `<span class="format-var">${text}</span>`;
      return [
        `<span class="format-label">Template Name:</span> Appointment Reminder<br>Hi ${v('{{patient_first_name}}')}, this is a reminder about your appointment on ${v('{{apt_date}}')} at ${v('{{site_name}}')}.`,
        `<span class="format-label">Template Name:</span> New Patient Welcome<br>Welcome to ${v('{{site_name}}')}! Your first appointment is on ${v('{{apt_date}}')}. Please arrive 15 min early.`,
        `<span class="format-label">Template Name:</span> Balance Reminder<br>Hi ${v('{{patient_first_name}}')}, you have a balance of ${v('{{patient_balance}}')}. Please call us at ${v('{{site_officephone}}')} to arrange payment.`,
      ];
    },

    variableOptions() {
      return [
        {
          label: 'Patient',
          options: [
            { value: '{{patient_first_name}}', label: '{{patient_first_name}}' },
            { value: '{{patient_last_name}}', label: '{{patient_last_name}}' },
            { value: '{{patient_name}}', label: '{{patient_name}} (full name)' },
            { value: '{{guarantor_first_name}}', label: '{{guarantor_first_name}}' },
            { value: '{{guarantor_last_name}}', label: '{{guarantor_last_name}}' },
            { value: '{{patient_balance}}', label: '{{patient_balance}}' },
          ]
        },
        {
          label: 'Appointment',
          options: [
            { value: '{{apt_date}}', label: '{{apt_date}} (date & time)' },
            { value: '{{apt_dayofweek}}', label: '{{apt_dayofweek}} (weekday)' },
            { value: '{{family_appts}}', label: '{{family_appts}}' },
            { value: '{{confirm_button}}', label: '{{confirm_button}}' },
          ]
        },
        {
          label: 'Practice',
          options: [
            { value: '{{site_name}}', label: '{{site_name}}' },
            { value: '{{site_officephone}}', label: '{{site_officephone}}' },
            { value: '{{site_address1}}', label: '{{site_address1}}' },
            { value: '{{site_address2}}', label: '{{site_address2}}' },
            { value: '{{site_city}}', label: '{{site_city}}' },
            { value: '{{site_state}}', label: '{{site_state}}' },
            { value: '{{site_zip}}', label: '{{site_zip}}' },
            // Email-only HTML block configured under Settings -> Email. Keep in sync with
            // WikiCreate.vue / WikiEdit.vue availableVariables and utils/templateVariables.js.
            { value: '{{email_signature_block}}', label: '{{email_signature_block}} (Email Signature)' },
          ]
        },
        {
          label: 'Staff / Payment',
          options: [
            // Legacy {{employee_name}} still resolves but is not offered as a mapping target —
            // an import should never introduce NEW uses of the deprecated spelling.
            { value: '{{employee_full_name}}', label: '{{employee_full_name}}' },
            { value: '{{employee_first_name}}', label: '{{employee_first_name}}' },
            { value: '{{total_amount}}', label: '{{total_amount}}' },
          ]
        },
        {
          label: 'Other',
          options: [
            { value: '__leave_as_is__', label: 'Leave as-is' },
            { value: '__remove__', label: 'Remove variable' },
          ]
        }
      ];
    },
  },

  beforeDestroy() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    if (this.kbPollTimer) {
      clearInterval(this.kbPollTimer);
      this.kbPollTimer = null;
    }
  },

  methods: {
    ...mapActions('wiki', ['parseDocument', 'parseDocuments', 'getParseResult', 'createWiki', 'getPresignedUploadUrl', 'saveUploadMeta']),

    show() {
      this.isModalVisible = true;
      this.$refs.modal.show();
      // If results are ready from background parsing, advance to review step
      if (this.parsedResult && this.currentStep <= 1) {
        this.currentStep = 2;
      }
    },

    hide() {
      this.isModalVisible = false;
      this.$refs.modal.hide();
    },

    onModalHidden() {
      this.isModalVisible = false;
      // Don't reset if parsing is in progress — keep polling in background
      if (!this.isParsing && !this.kbIsParsing) {
        this.resetForm();
      }
    },

    resetForm() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
      this.currentStep = 0;
      this.importType = null;
      this.isParsing = false;
      this.parseError = null;
      this.fileName = null;
      this.parsedResult = null;
      this.templates = [];
      this.variableMappings = {};
      this.hardcodedValues = [];
      this.isImporting = false;
      this.importProgress = 0;
      this.importTotal = 0;
      this.importResults = [];
      this.isDragOver = false;
      this.isModalVisible = false;
      // KB state
      this.kbFiles = [];
      this.kbIsParsing = false;
      this.kbParseError = null;
      this.kbJobId = null;
      if (this.kbPollTimer) { clearInterval(this.kbPollTimer); this.kbPollTimer = null; }
      this.kbProgress = null;
      this.kbDocuments = [];
      this.kbSummary = null;
      this.kbBulkTags = [];
      this.kbIsImporting = false;
      this.kbImportProgress = 0;
      this.kbImportTotal = 0;
      this.kbImportResults = [];
      this.kbIsDragOver = false;
    },

    goToStep(index) {
      // Only allow clicking to go back to completed steps
      if (index < this.currentStep) {
        this.currentStep = index;
      }
    },

    nextStep() {
      if (this.currentStep < this.steps.length - 1) {
        this.currentStep++;
      }
    },

    previousStep() {
      if (this.currentStep > 0) {
        this.currentStep--;
      }
    },

    // ------ Step 1: Upload ------

    onDragOver(e) {
      e.preventDefault();
      this.isDragOver = true;
    },

    onDragLeave(e) {
      e.preventDefault();
      this.isDragOver = false;
    },

    onDrop(e) {
      e.preventDefault();
      this.isDragOver = false;
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        this.handleFile(e.dataTransfer.files[0]);
      }
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    onFileSelected(e) {
      if (e.target.files && e.target.files[0]) {
        this.handleFile(e.target.files[0]);
      }
      // Reset file input so same file can be selected again
      e.target.value = '';
    },

    async handleFile(file) {
      // Validate file type
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];
      if (!validTypes.includes(file.type) && !file.name.endsWith('.docx')) {
        this.parseError = 'Please upload a .docx file. Other file types are not supported.';
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        this.parseError = 'File is too large. Maximum file size is 10MB.';
        return;
      }

      this.fileName = file.name;
      this.isParsing = true;
      this.parseError = null;

      try {
        // 1. Upload the file — backend extracts text, starts async Bedrock processing, returns jobId
        const base64 = await this.fileToBase64(file);
        const startResult = await this.parseDocument({
          file: base64,
          fileType: 'docx'
        });

        const jobId = startResult.jobId;
        if (!jobId) {
          throw new Error('Server did not return a job ID');
        }

        // 2. Poll for results every 3 seconds
        this.pollForResults(jobId);

      } catch (err) {
        this.isParsing = false;
        const msg = (err.response && err.response.data && err.response.data.message)
          ? err.response.data.message
          : (err.message || 'Failed to parse the document. Please try again.');
        this.parseError = msg;
      }
    },

    pollForResults(jobId) {
      let attempts = 0;
      const maxAttempts = 40; // 40 * 3s = 2 minutes max

      this.pollTimer = setInterval(async () => {
        attempts++;

        try {
          const statusData = await this.getParseResult(jobId);

          if (statusData.status === 'complete') {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
            this.onParseComplete(statusData.result);
          } else if (statusData.status === 'error') {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
            this.isParsing = false;
            this.parseError = statusData.error || 'AI parsing failed. Please try again.';
          } else if (attempts >= maxAttempts) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
            this.isParsing = false;
            this.parseError = 'Parsing is taking too long. Please try again with a smaller document.';
          }
          // else status is 'processing' — keep polling
        } catch (err) {
          // Network error during polling — keep trying unless we've exceeded max attempts
          if (attempts >= maxAttempts) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
            this.isParsing = false;
            this.parseError = 'Lost connection while parsing. Please try again.';
          }
        }
      }, 3000);
    },

    onParseComplete(result) {
      this.parsedResult = result;

      // Populate templates with UI-specific fields
      this.templates = (result.templates || []).map(t => ({
        ...t,
        included: true,
        _expanded: false
      }));

      // Populate variable mappings with userChoice defaults
      this.variableMappings = {};
      if (result.variableMappings) {
        Object.keys(result.variableMappings).forEach(varName => {
          const m = result.variableMappings[varName];
          this.$set(this.variableMappings, varName, {
            ...m,
            userChoice: m.suggestedMapping || '__leave_as_is__'
          });
        });
      }

      // Populate hardcoded values with apply=false by default
      this.hardcodedValues = (result.hardcodedValues || []).map(hv => ({
        ...hv,
        apply: false
      }));

      this.isParsing = false;

      // If modal was closed while parsing, show notification instead of auto-advancing
      if (!this.isModalVisible) {
        this.$bvToast.toast('Your templates are ready for review! Click Import to continue.', {
          title: 'Document Parsed',
          variant: 'success',
          solid: true,
          autoHideDelay: 15000,
        });
        return;
      }

      // Auto-advance to review step (step 2, since step 0 is type selection)
      this.currentStep = 2;
    },

    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          // Strip the data:...;base64, prefix
          const result = reader.result;
          const base64 = result.split(',')[1] || result;
          resolve(base64);
        };
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(file);
      });
    },

    clearError() {
      this.parseError = null;
    },

    downloadSample() {
      window.open('/static/sample-wiki-import.docx', '_blank');
    },

    // ------ Step 2: Review Templates ------

    toggleSelectAll(checked) {
      this.templates.forEach(t => {
        t.included = checked;
      });
    },

    toggleType(template, type) {
      // Cycle through types when clicking badges
      if (type === 'sms') {
        if (template.suggestedType === 'sms') {
          template.suggestedType = 'both';
        } else if (template.suggestedType === 'both') {
          template.suggestedType = 'email';
        } else {
          template.suggestedType = 'sms';
        }
      } else if (type === 'email') {
        if (template.suggestedType === 'email') {
          template.suggestedType = 'both';
        } else if (template.suggestedType === 'both') {
          template.suggestedType = 'sms';
        } else {
          template.suggestedType = 'email';
        }
      }
    },

    truncate(text, length) {
      if (!text || text.length <= length) return text;
      return text.substring(0, length) + '...';
    },

    // ------ Step 3: Map Variables ------

    countVariableUsage(varName) {
      return this.templates.filter(
        t => t.included && t.detectedVariables && t.detectedVariables.includes(varName)
      ).length;
    },

    // ------ Step 4: Confirm & Import ------

    applyMappings(body) {
      if (!body) return '';
      let result = body;

      // 1. Apply variable mappings
      for (const varName of Object.keys(this.variableMappings)) {
        const mapping = this.variableMappings[varName];
        const choice = mapping.userChoice;

        if (!choice || choice === '__leave_as_is__') {
          continue;
        }

        // Build regex to match [VarName], [?VarName], [VarName##anything] patterns
        const escapedVar = varName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const pattern = new RegExp(
          '\\[\\??' + escapedVar + '(?:##[^\\]]*)?\\]',
          'g'
        );

        if (choice === '__remove__') {
          result = result.replace(pattern, '');
        } else {
          result = result.replace(pattern, choice);
        }
      }

      // 2. Apply hardcoded value replacements
      for (const hv of this.hardcodedValues) {
        if (hv.apply && hv.value && hv.suggestedVariable) {
          // Escape the hardcoded value for use in regex
          const escapedValue = hv.value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const hvPattern = new RegExp(escapedValue, 'g');
          result = result.replace(hvPattern, hv.suggestedVariable);
        }
      }

      return result;
    },

    buildEditorContent(title, body) {
      const blocks = [
        {
          type: 'header',
          data: {
            text: title,
            level: 1
          }
        }
      ];

      // Split body into paragraphs on double-newline or single newline
      const paragraphs = body.split(/\n+/).filter(p => p.trim());
      for (const para of paragraphs) {
        blocks.push({
          type: 'paragraph',
          data: {
            text: para.trim()
          }
        });
      }

      return JSON.stringify({
        time: Date.now(),
        blocks: blocks,
        version: '2.22.2'
      });
    },

    async importTemplates() {
      this.isImporting = true;
      this.importProgress = 0;
      this.importTotal = this.includedTemplates.length;
      this.importResults = [];

      const templatesToImport = [...this.includedTemplates];

      for (const template of templatesToImport) {
        try {
          const mappedBody = this.applyMappings(template.body);
          const content = this.buildEditorContent(template.title, mappedBody);

          const payload = {
            PageToCreate: {
              Name: template.title,
              Content: content,
              SiteID: this.siteID,
              CreatedByEmployeeID: this.userID,
              CreatedAt: new Date().toISOString().slice(0, 19)
            },
            IsSmsTemplate: template.suggestedType === 'sms' || template.suggestedType === 'both',
            IsEmailTemplate: template.suggestedType === 'email' || template.suggestedType === 'both',
            IsPublic: false,
            Tags: null,
            UploadedImages: [],
            SiteID: this.siteID,
            employeesWhoCanEdit: [],
            AllEmployeesCanSee: 1,
            selectedLanguages: []
          };

          await this.createWiki(payload);

          this.importResults.push({
            title: template.title,
            success: true,
            error: null
          });
        } catch (err) {
          const errorMsg = (err.response && err.response.data && err.response.data.message)
            ? err.response.data.message
            : (err.message || 'Unknown error');

          this.importResults.push({
            title: template.title,
            success: false,
            error: errorMsg
          });
        }

        this.importProgress++;
      }

      this.isImporting = false;
    },

    finishImport() {
      this.hide();
      this.$emit('imported');
    },

    // ------ KB Path: Upload ------

    kbOnDragOver(e) {
      e.preventDefault();
      this.kbIsDragOver = true;
    },

    kbOnDragLeave(e) {
      e.preventDefault();
      this.kbIsDragOver = false;
    },

    kbOnDrop(e) {
      e.preventDefault();
      this.kbIsDragOver = false;
      if (e.dataTransfer.files) {
        this.kbAddFiles(Array.from(e.dataTransfer.files));
      }
    },

    kbOnFileSelected(e) {
      if (e.target.files) {
        this.kbAddFiles(Array.from(e.target.files));
      }
      e.target.value = '';
    },

    kbAddFiles(newFiles) {
      for (const file of newFiles) {
        const lower = file.name.toLowerCase();
        if (!lower.endsWith('.docx') && !lower.endsWith('.pdf')) continue;
        if (file.size > 25 * 1024 * 1024) {
          this.kbParseError = `File "${file.name}" exceeds 25 MB limit.`;
          return;
        }
        if (!this.kbFiles.some(f => f.name === file.name)) {
          this.kbFiles.push(file);
        }
      }
      if (this.kbFiles.length > 100) {
        this.kbFiles = this.kbFiles.slice(0, 100);
        this.kbParseError = 'Maximum 100 files allowed. Only the first 100 were kept.';
      }
    },

    formatFileSize(bytes) {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },

    async kbUploadAndParse() {
      this.kbIsParsing = true;
      this.kbParseError = null;
      this.kbProgress = null;

      try {
        const totalSize = this.kbTotalSizeBytes;

        if (totalSize < 3.5 * 1024 * 1024) {
          // Path A: Small batch — existing inline base64 path
          await this.kbUploadInline();
        } else {
          // Path B: Large batch — auto-zip + S3 presigned upload
          await this.kbUploadViaS3();
        }
      } catch (err) {
        this.kbIsParsing = false;
        this.kbParseError = extractApiError(err, 'Upload failed. Please try again.');
      }
    },

    async kbUploadInline() {
      const filesPayload = [];
      for (const file of this.kbFiles) {
        const base64 = await this.fileToBase64(file);
        filesPayload.push({ name: file.name, data: base64 });
      }

      const startResult = await this.parseDocuments({
        files: filesPayload,
        siteID: this.siteID
      });

      this.kbJobId = startResult.jobId;
      if (!this.kbJobId) {
        throw new Error('Server did not return a job ID');
      }

      this.kbPollForResults(this.kbJobId);
    },

    async kbUploadViaS3() {
      const JSZip = (await import('jszip')).default;

      // 1. Create zip
      this.kbProgress = { processed: 0, total: this.kbFiles.length, phase: 'zipping' };
      const zip = new JSZip();
      for (const file of this.kbFiles) {
        zip.file(file.name, file);
      }
      const zipBlob = await zip.generateAsync({ type: 'blob' });

      // 2. Get presigned URL
      this.kbProgress = { ...this.kbProgress, phase: 'uploading' };
      const { jobId, uploadUrl } = await this.getPresignedUploadUrl({
        siteID: this.siteID
      });
      this.kbJobId = jobId;

      // 3. Save metadata so coordinator knows which site this belongs to
      await this.saveUploadMeta({
        jobId,
        siteID: this.siteID,
        fileCount: this.kbFiles.length
      });

      // 4. Store jobId in localStorage for resume capability
      localStorage.setItem('teamio_wiki_bulk_job', JSON.stringify({
        jobId,
        siteID: this.siteID,
        timestamp: Date.now()
      }));

      // 5. Upload zip to S3 via presigned POST
      const formData = new FormData();
      Object.keys(uploadUrl.fields).forEach(key => {
        formData.append(key, uploadUrl.fields[key]);
      });
      formData.append('file', zipBlob);

      // uploadUrl.url is an absolute S3 URL — axios won't prepend baseURL
      // Don't set Content-Type explicitly — browser adds boundary with FormData
      await axios.post(uploadUrl.url, formData);

      // 6. S3 event triggers coordinator automatically — start polling
      this.kbProgress = { processed: 0, total: this.kbFiles.length, phase: 'processing' };
      this.kbPollForResults(jobId);
    },

    kbPollForResults(jobId) {
      let lastProcessed = 0;
      let stalledSince = null;

      this.kbPollTimer = setInterval(async () => {
        try {
          const statusData = await this.getParseResult(jobId);

          if (statusData.progress) {
            this.kbProgress = statusData.progress;
          }

          if (statusData.status === 'complete') {
            clearInterval(this.kbPollTimer);
            this.kbPollTimer = null;
            localStorage.removeItem('teamio_wiki_bulk_job');
            this.kbOnParseComplete(statusData.result);
          } else if (statusData.status === 'error') {
            clearInterval(this.kbPollTimer);
            this.kbPollTimer = null;
            localStorage.removeItem('teamio_wiki_bulk_job');
            if (statusData.partialResults && statusData.partialResults.length > 0) {
              this.kbOnParseComplete({
                documents: statusData.partialResults,
                summary: {
                  total: this.kbFiles.length,
                  success: statusData.partialResults.filter(d => d.status === 'success').length,
                  failed: statusData.partialResults.filter(d => d.status === 'failed').length
                }
              });
              return;
            }
            this.kbIsParsing = false;
            this.kbParseError = statusData.error || 'Processing failed. Please try again.';
          } else {
            // Stall detection
            const currentProcessed = statusData.progress ? statusData.progress.processed : 0;
            if (currentProcessed > lastProcessed) {
              lastProcessed = currentProcessed;
              stalledSince = null;
            } else {
              if (!stalledSince) {
                stalledSince = Date.now();
              } else if (Date.now() - stalledSince > 120000) {
                clearInterval(this.kbPollTimer);
                this.kbPollTimer = null;
                localStorage.removeItem('teamio_wiki_bulk_job');
                if (statusData.partialResults && statusData.partialResults.length > 0) {
                  this.kbOnParseComplete({
                    documents: statusData.partialResults,
                    summary: {
                      total: this.kbFiles.length,
                      success: statusData.partialResults.filter(d => d.status === 'success').length,
                      failed: this.kbFiles.length - statusData.partialResults.filter(d => d.status === 'success').length
                    }
                  });
                } else {
                  this.kbIsParsing = false;
                  this.kbParseError = 'Processing appears to have stalled. Please try again with fewer files.';
                }
              }
            }
          }
        } catch (err) {
          // Network error — keep trying
        }
      }, 3000);
    },

    kbOnParseComplete(result) {
      this.kbDocuments = (result.documents || []).map(d => ({
        ...d,
        selected: d.status === 'success',
        editTitle: d.title || d.filename.replace(/\.(docx|pdf)$/i, ''),
      }));
      this.kbSummary = result.summary;
      this.kbIsParsing = false;

      if (!this.isModalVisible) {
        this.$bvToast.toast(
          `${result.summary.success} of ${result.summary.total} documents are ready for review!`,
          { title: 'Documents Parsed', variant: 'success', solid: true, autoHideDelay: 15000 }
        );
        return;
      }

      this.currentStep = 2;
    },

    // ------ KB Path: Review ------

    kbToggleSelectAll(checked) {
      this.kbDocuments.forEach(d => {
        if (d.status === 'success') {
          d.selected = checked;
        }
      });
    },

    // ------ KB Path: Import ------

    async kbImportDocuments() {
      const docsToImport = this.kbDocuments.filter(d => d.selected && d.status === 'success');
      this.kbIsImporting = true;
      this.kbImportProgress = 0;
      this.kbImportTotal = docsToImport.length;
      this.kbImportResults = [];

      for (const doc of docsToImport) {
        try {
          const payload = {
            PageToCreate: {
              Name: doc.editTitle || doc.title,
              Content: JSON.stringify(doc.editorJsContent),
              SiteID: this.siteID,
              CreatedByEmployeeID: this.userID,
              CreatedAt: new Date().toISOString().slice(0, 19)
            },
            Tags: null,
            UploadedImages: [],
            SiteID: this.siteID,
            IsSmsTemplate: false,
            IsEmailTemplate: false,
            IsPublic: false,
            employeesWhoCanEdit: [],
            AllEmployeesCanSee: 1,
            selectedLanguages: []
          };

          await this.createWiki(payload);

          this.kbImportResults.push({
            title: doc.editTitle || doc.title,
            success: true,
            error: null
          });
        } catch (err) {
          const errorMsg = (err.response && err.response.data && err.response.data.message)
            ? err.response.data.message
            : (err.message || 'Unknown error');

          this.kbImportResults.push({
            title: doc.editTitle || doc.title,
            success: false,
            error: errorMsg
          });
        }

        this.kbImportProgress++;
      }

      this.kbIsImporting = false;
    }
  }
};
</script>

<style src="./WikiImportModal.scss" lang="scss" scoped></style>
